"use client";

import { signIn } from "next-auth/react";
import { useMemo, useState } from "react";
import { Button } from "~/shadcn/ui/button";
import { Input } from "~/shadcn/ui/input";
import { Label } from "~/shadcn/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/shadcn/ui/select";
import { Separator } from "~/shadcn/ui/separator";

type AccountType = "MUSICIAN" | "RESEARCHER" | "ORGANIZATION";

type Props = {
  lang: AppLocale;
};

export default function AuthWithTestCredentials({ lang }: Props) {
  const callbackUrl = useMemo(() => `/${lang}/dashboard`, [lang]);

  const [email, setEmail] = useState("e2e@example.com");
  const [password, setPassword] = useState("");
  const [accountType, setAccountType] = useState<AccountType>("MUSICIAN");
  const [error, setError] = useState<string | null>(null);
  const [isPending, setIsPending] = useState(false);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsPending(true);
    setError(null);
    try {
      const res = await signIn("test-credentials", {
        email,
        password,
        accountType,
        callbackUrl,
        redirect: false,
      });

      if (res?.error) {
        setError(res.error);
        return;
      }

      // Successful sign-in: navigate manually.
      window.location.assign(callbackUrl);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setIsPending(false);
    }
  };

  return (
    <div className="flex flex-col gap-2 mt-8" data-cy="test-credentials-login">
      <div className="flex items-center justify-center space-x-4 my-4">
        <Separator className="flex-1" />
        <p className="text-center text-secondary-foreground text-sm">
          Test-inloggning
        </p>
        <Separator className="flex-1" />
      </div>

      <form onSubmit={onSubmit} className="flex flex-col gap-3">
        <div className="grid gap-1">
          <Label htmlFor="test-email">Email</Label>
          <Input
            id="test-email"
            data-cy="test-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="username"
            required
          />
        </div>

        <div className="grid gap-1">
          <Label htmlFor="test-password">Lösenord</Label>
          <Input
            id="test-password"
            data-cy="test-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
          />
        </div>

        <div className="grid gap-1">
          <Label>Kontotyp</Label>
          <Select
            value={accountType}
            onValueChange={(v) => setAccountType(v as AccountType)}
          >
            <SelectTrigger data-cy="test-accountType">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="MUSICIAN">MUSICIAN</SelectItem>
              <SelectItem value="RESEARCHER">RESEARCHER</SelectItem>
              <SelectItem value="ORGANIZATION">ORGANIZATION</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {error && <p className="text-sm text-destructive">{error}</p>}

        <Button type="submit" disabled={isPending} data-cy="test-login-submit">
          {isPending ? "Loggar in..." : "Logga in"}
        </Button>
      </form>
    </div>
  );
}
